import { useCallback, useEffect, useRef, useState } from 'react'
import type { Entry } from '../shared/types'
import { api, ApiError } from './api'
import { pushError } from './toast'

// Debounced per-entry autosave shared by the client grid and the day view.
// - merges rapid field edits per entry id, flushes ~400ms after the last change
// - flushes immediately on demand (blur / navigation / Enter)
// - a failed save is NEVER dropped: it is re-queued and surfaced persistently
//   (hasError), so a billable edit is never silently lost.
export function useAutosave(opts: {
  onSaved?: (id: number, patch: Partial<Entry>) => void
  onChanged?: () => void
}) {
  const { onSaved, onChanged } = opts
  const pending = useRef<Map<number, Partial<Entry>>>(new Map())
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const [savingCount, setSavingCount] = useState(0)
  const [hasError, setHasError] = useState(false)

  const savedRef = useRef(onSaved)
  const changedRef = useRef(onChanged)
  savedRef.current = onSaved
  changedRef.current = onChanged

  const flush = useCallback(async (id: number) => {
    const patch = pending.current.get(id)
    if (!patch) return
    pending.current.delete(id)
    const t = timers.current.get(id)
    if (t) clearTimeout(t)
    timers.current.delete(id)

    setSavingCount((c) => c + 1)
    try {
      await api.updateEntry(id, patch)
      savedRef.current?.(id, patch)
      setHasError(false)
      changedRef.current?.()
    } catch (e) {
      pending.current.set(id, { ...patch, ...(pending.current.get(id) ?? {}) })
      setHasError(true)
      const msg =
        e instanceof ApiError && e.status === 409
          ? 'This entry is on an issued invoice and can no longer be edited.'
          : `Save failed — your last change is not saved. ${(e as Error).message}`
      pushError(msg)
    } finally {
      setSavingCount((c) => c - 1)
    }
  }, [])

  const scheduleSave = useCallback(
    (id: number, patch: Partial<Entry>) => {
      const cur = pending.current.get(id) ?? {}
      pending.current.set(id, { ...cur, ...patch })
      const t = timers.current.get(id)
      if (t) clearTimeout(t)
      timers.current.set(id, setTimeout(() => flush(id), 400))
    },
    [flush],
  )

  const flushAll = useCallback(() => {
    for (const id of [...pending.current.keys()]) flush(id)
  }, [flush])

  useEffect(() => {
    const handler = () => flushAll()
    window.addEventListener('beforeunload', handler)
    return () => {
      window.removeEventListener('beforeunload', handler)
      flushAll()
    }
  }, [flushAll])

  return { scheduleSave, flush, flushAll, savingCount, hasError, setHasError }
}
